import { BookIntervalCode } from "@/types";
import { IPaymentDetailInfo } from "@/domain/entities/payment";
import {
  IPaymentInfo,
  IPaymentInfoType,
} from "@/domain/entities/payment/paymentInfo.types";
import { IResultData } from "@/domain/entities/shared/result";
import TYPES from "@/ioc/identifiers";
import { container } from "@/ioc/inversify.config";
import { IPaymentRepository } from "@/repositories/payment/paymentRepository.types";
import { defineStore } from "pinia";
import useSetResultCodeIfNeeded from "@/use/useSetResultCodeIfNeeded";
import { IPaymentMethod } from "@/domain/entities/payment/paymentMethod.types";
import { GetPaymentMethodsUseCase } from "@/domain/usecases/payment/interface/GetPaymentMethodsUseCase";
import { IsBookableStateUseCase } from "@/domain/usecases/payment/interface/IsBookableStateUseCase";
import { IDateInfo, IKakaoPayBookInfo } from "@/domain/entities/book";
import { RequestKakaoPayUseCase } from "@/domain/usecases/payment/RequestKakaoPay";
import { RequestFreeBookUseCase } from "@/domain/usecases/payment/interface/RequestFreeBookUseCase";
import { RequestFailFreeBookUseCase } from "@/domain/usecases/payment/RequestFailFreeBook";
import { RequestCancelFreeBookUseCase } from "@/domain/usecases/payment/RequestCancelFreeBook";

interface IPaymentState {
  paymentInfos: IPaymentInfo[];
}

const paymentRepository = () => {
  return container.get<IPaymentRepository>(TYPES.IPaymentRepository);
};

const getPaymentMethodsUseCase = () =>
  container.get<GetPaymentMethodsUseCase>(TYPES.GetPaymentMethodsUseCase);

const isBookableStateUseCase = container.get<IsBookableStateUseCase>(
  TYPES.IsBookableStateUseCase
);

const requestKakaoPay = () =>
  container.get<RequestKakaoPayUseCase>(TYPES.RequestKakaoPayUseCase);

const requestFreeBook = () =>
  container.get<RequestFreeBookUseCase>(TYPES.RequestFreeBookUseCase);

const requestFailFreeBook = () =>
  container.get<RequestFailFreeBookUseCase>(TYPES.RequestFailFreeBookUseCase);

const requestCancelFreeBook = () =>
  container.get<RequestCancelFreeBookUseCase>(
    TYPES.RequestCancelFreeBookUseCase
  );

export const usePaymentStore = defineStore("payment", {
  state: (): IPaymentState => {
    return {
      paymentInfos: [],
    };
  },
  getters: {},
  actions: {
    async getPaymentInfos(
      dateFrom: string,
      dateTo: string,
      paymentType?: IPaymentInfoType
    ): Promise<IResultData<IPaymentInfo[]>> {
      const paymentInfosResult: IResultData<IPaymentInfo[]> = {
        isSuccess: false,
        data: [],
      };

      await paymentRepository()
        .getPaymentInfos(dateFrom, dateTo, paymentType)
        .then((result) => {
          result
            .map((paymentInfos) => {
              paymentInfosResult.isSuccess = true;
              paymentInfosResult.data = paymentInfos;
            })
            .mapErr((error) => {
              useSetResultCodeIfNeeded(paymentInfosResult, error);
              paymentInfosResult.errorMessage = error.message;
            });
        })
        .catch((error) => {
          paymentInfosResult.errorMessage = error.message;
        });

      this.paymentInfos = paymentInfosResult.data ?? [];
      return paymentInfosResult;
    },

    async getPaymentDetailInfo(
      payUid: string
    ): Promise<IResultData<IPaymentDetailInfo>> {
      const paymentDetailResult: IResultData<IPaymentDetailInfo> = {
        isSuccess: false,
      };

      await paymentRepository()
        .getPaymentDetailInfo(payUid)
        .then((result) => {
          result
            .map((paymentDetailInfo) => {
              paymentDetailResult.isSuccess = true;
              paymentDetailResult.data = paymentDetailInfo;
            })
            .mapErr((error) => {
              useSetResultCodeIfNeeded(paymentDetailResult, error);
              paymentDetailResult.errorMessage = error.message;
            });
        })
        .catch((error) => {
          paymentDetailResult.errorMessage = error.message;
        });

      return paymentDetailResult;
    },

    async getPaymentMethods(
      turnId?: string
    ): Promise<IResultData<IPaymentMethod[]>> {
      return await getPaymentMethodsUseCase().execute(turnId);
    },

    isBookableState(
      totalAmount: number | undefined,
      bookInProgress: boolean,
      seatsSelectable: boolean,
      bookingDates: IDateInfo[],
      shouldPay: boolean,
      bookIntervalCode?: BookIntervalCode,
      realBookableDatesLength?: number
    ): boolean {
      return isBookableStateUseCase.execute(
        totalAmount,
        bookInProgress,
        seatsSelectable,
        bookingDates,
        shouldPay,
        bookIntervalCode,
        realBookableDatesLength
      );
    },

    async requestKakaoPay(
      autoSeatAssgin: boolean,
      seatsSelectable: boolean,
      turnId: string,
      getOnStationId: string,
      getOffStationId: string,
      dateInfos: IDateInfo[],
      isMonthlyBook: boolean
    ): Promise<IResultData<IKakaoPayBookInfo>> {
      return requestKakaoPay().execute(
        autoSeatAssgin,
        seatsSelectable,
        turnId,
        getOnStationId,
        getOffStationId,
        dateInfos,
        isMonthlyBook
      );
    },

    async requestFreeBook(
      autoSeatAssgin: boolean,
      seatsSelectable: boolean,
      turnId: string,
      getOnStationId: string,
      getOffStationId: string,
      dateInfos: IDateInfo[],
      isMonthlyBook: boolean
    ): Promise<IResultData<string>> {
      return requestFreeBook().execute(
        autoSeatAssgin,
        seatsSelectable,
        turnId,
        getOnStationId,
        getOffStationId,
        dateInfos,
        isMonthlyBook
      );
    },

    async requestFailFreeBook(bookId: string): Promise<IResultData<boolean>> {
      return requestFailFreeBook().execute(bookId);
    },

    async requestCancelFreeBook(
      bookId: string,
      bookDetailId: string
    ): Promise<IResultData<boolean>> {
      return requestCancelFreeBook().execute(bookId, bookDetailId);
    },
  },
});
